import { useState, useEffect, useCallback, useRef } from "react";
import userServices from "../services/userServices";

/**
 * Custom hook to manage compliance queue states (pagination, filters, debounced search, sorting, and reloading).
 */
export function useModerationQueue(queueType, initialFilters = {}) {
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState(queueType === "verification" ? "fixDeadline" : "createdAt");
  const [sortOrder, setSortOrder] = useState(queueType === "verification" ? "asc" : "desc");
  const [filters, setFilters] = useState(initialFilters);

  const fetchIdRef = useRef(0);

  // Debounce search query changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit,
        sort: sortField,
        order: sortOrder,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...filters
      };

      let result;
      if (queueType === "verification") {
        result = await userServices.getVerification(params);
      } else if (queueType === "behavioral") {
        result = await userServices.getBehavior(params);
      } else if (queueType === "appeals") {
        result = await userServices.getAppeals(params);
      }

      if (fetchId !== fetchIdRef.current) return;

      if (result && result.success) {
        setData(result.data || []);
        setTotalItems(result.meta?.totalItems || 0);
      } else {
        setError(result?.error || "Failed to fetch data");
        setData([]);
      }
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      console.error(`Error in useModerationQueue for ${queueType}:`, err);
      setError("An unexpected error occurred");
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [queueType, page, limit, debouncedSearch, sortField, sortOrder, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    window.addEventListener("moderationAction", fetchData);
    return () => {
      window.removeEventListener("moderationAction", fetchData);
    };
  }, [fetchData]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setFilters(initialFilters);
    setPage(1);
  };

  return {
    data,
    totalItems,
    loading,
    error,
    page,
    limit,
    search,
    setSearch,
    sortField,
    sortOrder,
    filters,
    handlePageChange,
    handleLimitChange,
    handleSort,
    handleFilterChange,
    clearFilters,
    refresh: fetchData
  };
}

/**
 * Custom hook to load and cache flag timelines.
 */
export function useTimeline(flagId) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTimeline = useCallback(async () => {
    if (!flagId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await userServices.getTimeline(flagId);
      if (res && res.success) {
        setTimeline(res.data || []);
      } else {
        setError(res?.error || "Failed to fetch timeline");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [flagId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  return {
    timeline,
    loading,
    error,
    refresh: fetchTimeline
  };
}

/**
 * Custom hook to manage the moderation dashboard counts and caches.
 */
export function useDashboard() {
  const [stats, setStats] = useState({
    activeVerification: 0,
    pendingAppeals: 0,
    criticalBehavioral: 0,
    escalatedDisputes: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await userServices.getDashboard();
      if (res && res.success) {
        setStats(res.data);
      } else {
        setError(res?.error || "Failed to load dashboard metrics");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    window.addEventListener("moderationAction", fetchStats);
    return () => {
      window.removeEventListener("moderationAction", fetchStats);
    };
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats
  };
}
